import { useMemo, useState } from "react";
import { styles } from "../styles";
import { storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { createFacilityServiceTicket } from "../models/models";
import BackButton from "../components/BackButton";
import { isCameraNative, takePhotoNative, pickPhotosNative } from "../utils/photoPicker";
import { printInvoice, money } from "./FacilityJobsPage";

const STATUS_LABEL = {
  requested: "Requested",
  unassigned: "Unassigned",
  assigned: "Assigned",
  in_progress: "In Progress",
  closed: "Completed",
  canceled: "Cancelled",
};

const isAdminOrManager = (role) =>
  role === "executive" || role === "facilities_manager";

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export default function FacilityJobDetailPage({
  job,
  customers = [],
  facilityServiceTicketsRaw = [],
  publicInvoicesRaw = [],
  actions,
  user,
  role,
  orgId,
  onBack,
}) {
  // Find live building / customer for header display. Also surfaces the
  // building + customer objects so callers (printInvoice etc.) can pull
  // address + billing fields without re-walking the customer tree.
  const { buildingName, customerName, equipmentList, building, customer } =
    useMemo(() => {
      for (const c of customers) {
        for (const b of c.locations || []) {
          if (b.id === job.locationId) {
            return {
              buildingName: b.name || "—",
              customerName: c.name || "—",
              equipmentList: b.equipment || [],
              building: b,
              customer: c,
            };
          }
        }
      }
      return {
        buildingName: "—",
        customerName: "—",
        equipmentList: [],
        building: null,
        customer: null,
      };
    }, [customers, job.locationId]);

  const equipmentName = (eqId) => {
    const eq = equipmentList.find((e) => e.id === eqId);
    return eq ? `${eq.type}${eq.subType ? ` (${eq.subType})` : ""}` : "—";
  };

  const tickets = useMemo(
    () =>
      facilityServiceTicketsRaw
        .filter((t) => t.jobId === job.id)
        .sort(
          (a, b) =>
            new Date(b.createdAt || b.workDate) -
            new Date(a.createdAt || a.workDate)
        ),
    [facilityServiceTicketsRaw, job.id]
  );

  // --- Filing a new ticket ---
  const [showForm, setShowForm] = useState(false);
  const [workDate, setWorkDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [summary, setSummary] = useState("");
  const [labor, setLabor] = useState([
    { id: newId(), employeeName: "", hours: "", date: workDate },
  ]);
  const [materials, setMaterials] = useState([
    { id: newId(), name: "", partNumber: "", qty: "" },
  ]);
  const [notes, setNotes] = useState("");
  const [photoFiles, setPhotoFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const resetForm = () => {
    const today = new Date().toISOString().slice(0, 10);
    setWorkDate(today);
    setSummary("");
    setLabor([
      { id: newId(), employeeName: "", hours: "", date: today },
    ]);
    setMaterials([{ id: newId(), name: "", partNumber: "", qty: "" }]);
    setNotes("");
    setPhotoFiles([]);
    setShowForm(false);
    setErr("");
  };

  const uploadPhotos = async (ticketId) => {
    const uploaded = [];
    for (const file of photoFiles) {
      const path = `organizations/${orgId}/facilityTicketPhotos/${ticketId}/${Date.now()}-${file.name}`;
      const r = ref(storage, path);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);
      uploaded.push({ url, path, name: file.name });
    }
    return uploaded;
  };

  const handleSave = async () => {
    if (!summary.trim()) {
      setErr("Summary is required.");
      return;
    }
    const cleanedLabor = labor.filter(
      (l) => (l.employeeName || "").trim() || (l.hours || "").toString().trim()
    );
    const cleanedMaterials = materials.filter(
      (m) =>
        (m.name || "").trim() ||
        (m.partNumber || "").trim() ||
        (m.qty || "").toString().trim()
    );

    try {
      setBusy(true);
      setErr("");
      const base = createFacilityServiceTicket(job.id);
      const photos = await uploadPhotos(base.id);
      await actions.addFacilityServiceTicket({
        ...base,
        workDate,
        summary: summary.trim(),
        labor: cleanedLabor,
        materials: cleanedMaterials,
        notes: notes.trim(),
        photos,
      });
      resetForm();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const acknowledgeTicket = (t) =>
    actions.updateFacilityServiceTicket(t.id, {
      acknowledged: true,
      acknowledgedAt: new Date().toISOString(),
      acknowledgedBy: user?.email || "",
    });

  const deleteTicket = (t) => {
    if (
      !window.confirm(
        `Delete this service ticket from ${t.workDate}? This cannot be undone.`
      )
    )
      return;
    actions.deleteFacilityServiceTicket(t.id);
  };

  const canManage = isAdminOrManager(role);
  const isAssignedToMe = (job.assignedTechs || []).includes(user?.uid);
  // Same gate the old in-place expand used: can act on status if
  // you're admin/manager OR an assigned tech.
  const canChangeStatus = canManage || isAssignedToMe;

  const startJob = () =>
    actions.updateJob(job.id, {
      status: "in_progress",
      updatedAt: new Date().toISOString(),
    });
  const completeJob = () =>
    actions.updateJob(job.id, {
      status: "closed",
      closedAt: new Date().toISOString(),
      closedBy: user?.email || "unknown",
      updatedAt: new Date().toISOString(),
    });
  const cancelJob = () => {
    if (!window.confirm("Cancel this work order? It moves into Work Records."))
      return;
    actions.updateJob(job.id, {
      status: "canceled",
      closedAt: new Date().toISOString(),
      closedBy: user?.email || "unknown",
      updatedAt: new Date().toISOString(),
    });
  };
  const reopenJob = () =>
    actions.updateJob(job.id, {
      status: (job.assignedTechs || []).length > 0 ? "assigned" : "unassigned",
      closedAt: null,
      closedBy: null,
      updatedAt: new Date().toISOString(),
    });

  return (
    <>
      <BackButton onClick={onBack} />

      <h2 style={{ margin: "8px 0 4px" }}>
        {job.jobNumber ? `#${job.jobNumber} — ` : ""}
        {job.title}
      </h2>
      <div style={{ fontSize: "13px", color: "#666", marginBottom: "12px" }}>
        {customerName} · {buildingName} · {STATUS_LABEL[job.status] || job.status}
      </div>

      {/* Invoice — present once the contractor finalizes the bill on this
          job. Surfaces a one-tap View / Print so a facility user landing
          here from the "Invoice ready" notification immediately sees the
          number and can save/email a copy. */}
      {(() => {
        const inv = publicInvoicesRaw.find(
          (i) => i.jobId === job.id && !i.sourceDeleted
        );
        if (!inv) return null;
        return (
          <div
            style={{
              ...styles.card,
              maxWidth: "780px",
              background: "#eef7ff",
              border: "1px solid #b6daff",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                color: "#1a5fb4",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "4px",
              }}
            >
              Invoice
            </div>
            <div style={{ fontSize: "15px", marginBottom: "8px" }}>
              <strong>Total due: {money(inv.grandTotal)}</strong>
              {inv.jobNumber ? (
                <span style={{ color: "#666", marginLeft: "8px" }}>
                  (Job #{inv.jobNumber})
                </span>
              ) : null}
            </div>
            <button
              style={styles.button}
              onClick={() => printInvoice(inv, building, customer)}
            >
              View / Print Invoice
            </button>
          </div>
        );
      })()}

      <div style={{ ...styles.card, maxWidth: "780px" }}>
        {job.description && (
          <div style={{ marginBottom: "8px" }}>
            <strong style={{ fontSize: "12px", color: "#666" }}>
              Details
            </strong>
            <div style={{ fontSize: "13px", whiteSpace: "pre-wrap" }}>
              {job.description}
            </div>
          </div>
        )}
        {(job.equipmentIds || []).length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <strong style={{ fontSize: "12px", color: "#666" }}>
              Equipment
            </strong>
            <ul style={{ margin: "2px 0 0 16px", padding: 0, fontSize: "13px" }}>
              {(job.equipmentIds || []).map((id) => (
                <li key={id}>{equipmentName(id)}</li>
              ))}
            </ul>
          </div>
        )}
        {job.otherEquipment && (
          <div style={{ fontSize: "13px", marginBottom: "8px" }}>
            <span
              style={{
                background: "#eef",
                color: "#33c",
                fontSize: "11px",
                padding: "2px 8px",
                borderRadius: "10px",
                fontWeight: 600,
              }}
            >
              Other / non-equipment work
            </span>
          </div>
        )}

        {canChangeStatus && (
          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              marginTop: "8px",
              paddingTop: "8px",
              borderTop: "1px solid #eee",
            }}
          >
            {(job.status === "assigned" || job.status === "unassigned") &&
              canChangeStatus && (
                <button
                  style={{ ...styles.primaryButton, margin: 0 }}
                  onClick={startJob}
                >
                  Start Work
                </button>
              )}
            {job.status === "in_progress" && canChangeStatus && (
              <button
                style={{ ...styles.primaryButton, margin: 0 }}
                onClick={completeJob}
              >
                Mark Complete
              </button>
            )}
            {(job.status === "closed" || job.status === "canceled") &&
              canManage && (
                <button
                  style={{ ...styles.button, margin: 0 }}
                  onClick={reopenJob}
                >
                  Reopen
                </button>
              )}
            {job.status !== "closed" &&
              job.status !== "canceled" &&
              canManage && (
                <button
                  style={{
                    ...styles.button,
                    margin: 0,
                    background: "#fee",
                    color: "#a00",
                  }}
                  onClick={cancelJob}
                >
                  Cancel Work Order
                </button>
              )}
          </div>
        )}
      </div>

      <h3 style={{ margin: "20px 0 6px" }}>Service Tickets ({tickets.length})</h3>

      {!showForm ? (
        <button
          style={styles.primaryButton}
          onClick={() => setShowForm(true)}
        >
          + File Service Ticket
        </button>
      ) : (
        <div style={{ ...styles.card, maxWidth: "780px" }}>
          <h4 style={{ marginTop: 0 }}>File Service Ticket</h4>
          {err && (
            <div style={{ color: "red", fontSize: "12px", marginBottom: "6px" }}>
              {err}
            </div>
          )}

          <div style={{ marginBottom: "8px" }}>
            <label style={{ fontSize: "12px", color: "#666" }}>
              Work date
            </label>
            <br />
            <input
              type="date"
              style={{ ...styles.input, maxWidth: "200px" }}
              value={workDate}
              onChange={(e) => setWorkDate(e.target.value)}
            />
          </div>

          <div style={{ marginBottom: "8px" }}>
            <label style={{ fontSize: "12px", color: "#666" }}>
              Summary of work done
            </label>
            <input
              style={{ ...styles.input, width: "100%" }}
              placeholder="e.g. Replaced 6 hallway light bulbs on 2nd floor"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
            />
          </div>

          <div style={{ marginBottom: "8px" }}>
            <strong style={{ fontSize: "12px", color: "#666" }}>Labor</strong>
            {labor.map((l, i) => (
              <div
                key={l.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(110px, 1fr) 80px 120px auto",
                  gap: "6px",
                  marginTop: "4px",
                }}
              >
                <input
                  style={{ ...styles.input, margin: 0 }}
                  type="text"
                  placeholder="Employee"
                  value={l.employeeName}
                  onChange={(e) => {
                    const next = labor.slice();
                    next[i] = { ...l, employeeName: e.target.value };
                    setLabor(next);
                  }}
                />
                <input
                  style={{ ...styles.input, margin: 0 }}
                  type="number"
                  min="0"
                  step="0.25"
                  placeholder="Hours"
                  value={l.hours}
                  onChange={(e) => {
                    const next = labor.slice();
                    next[i] = { ...l, hours: e.target.value };
                    setLabor(next);
                  }}
                />
                <input
                  style={{ ...styles.input, margin: 0 }}
                  type="date"
                  value={l.date}
                  onChange={(e) => {
                    const next = labor.slice();
                    next[i] = { ...l, date: e.target.value };
                    setLabor(next);
                  }}
                />
                <button
                  style={{
                    ...styles.button,
                    margin: 0,
                    background: "#fee",
                    color: "#a00",
                  }}
                  onClick={() => setLabor(labor.filter((_, idx) => idx !== i))}
                  disabled={labor.length === 1}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              style={{ ...styles.button, marginTop: "4px" }}
              onClick={() =>
                setLabor([
                  ...labor,
                  { id: newId(), employeeName: "", hours: "", date: workDate },
                ])
              }
            >
              + Add labor row
            </button>
          </div>

          <div style={{ marginBottom: "8px" }}>
            <strong style={{ fontSize: "12px", color: "#666" }}>
              Materials
            </strong>
            {materials.map((m, i) => (
              <div
                key={m.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(110px, 1fr) 100px 70px auto",
                  gap: "6px",
                  marginTop: "4px",
                }}
              >
                <input
                  style={{ ...styles.input, margin: 0 }}
                  type="text"
                  placeholder="Item name"
                  value={m.name}
                  onChange={(e) => {
                    const next = materials.slice();
                    next[i] = { ...m, name: e.target.value };
                    setMaterials(next);
                  }}
                />
                <input
                  style={{ ...styles.input, margin: 0 }}
                  type="text"
                  placeholder="Part # (opt.)"
                  value={m.partNumber}
                  onChange={(e) => {
                    const next = materials.slice();
                    next[i] = { ...m, partNumber: e.target.value };
                    setMaterials(next);
                  }}
                />
                <input
                  style={{ ...styles.input, margin: 0 }}
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Qty"
                  value={m.qty}
                  onChange={(e) => {
                    const next = materials.slice();
                    next[i] = { ...m, qty: e.target.value };
                    setMaterials(next);
                  }}
                />
                <button
                  style={{
                    ...styles.button,
                    margin: 0,
                    background: "#fee",
                    color: "#a00",
                  }}
                  onClick={() =>
                    setMaterials(materials.filter((_, idx) => idx !== i))
                  }
                  disabled={materials.length === 1}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              style={{ ...styles.button, marginTop: "4px" }}
              onClick={() =>
                setMaterials([
                  ...materials,
                  { id: newId(), name: "", partNumber: "", qty: "" },
                ])
              }
            >
              + Add material row
            </button>
          </div>

          <div style={{ marginBottom: "8px" }}>
            <label style={{ fontSize: "12px", color: "#666" }}>
              Notes (optional)
            </label>
            <textarea
              style={{ ...styles.textarea, width: "100%" }}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div style={{ marginBottom: "8px" }}>
            <label style={{ fontSize: "12px", color: "#666" }}>
              Photos (optional)
            </label>
            <br />
            {isCameraNative() ? (
              <span style={{ display: "inline-flex", gap: "8px" }}>
                <button
                  type="button"
                  style={{ ...styles.button, margin: 0 }}
                  onClick={async () => {
                    try {
                      const file = await takePhotoNative();
                      if (file) setPhotoFiles((prev) => [...prev, file]);
                    } catch {}
                  }}
                >
                  📷 Camera
                </button>
                <button
                  type="button"
                  style={{ ...styles.button, margin: 0 }}
                  onClick={async () => {
                    try {
                      const files = await pickPhotosNative(10);
                      if (files.length) setPhotoFiles((prev) => [...prev, ...files]);
                    } catch {}
                  }}
                >
                  🖼️ Gallery
                </button>
              </span>
            ) : (
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={(e) => setPhotoFiles(Array.from(e.target.files))}
              />
            )}
            {photoFiles.length > 0 && (
              <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                {photoFiles.length} photo(s) selected
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
            <button
              style={{ ...styles.primaryButton, margin: 0 }}
              onClick={handleSave}
              disabled={busy || !summary.trim()}
            >
              {busy ? "Saving..." : "Save Ticket"}
            </button>
            <button
              style={{ ...styles.button, margin: 0 }}
              onClick={resetForm}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {tickets.length === 0 ? (
        <p style={{ color: "#666", fontStyle: "italic", marginTop: "12px" }}>
          No service tickets filed for this work order yet.
        </p>
      ) : (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: "12px 0 0",
          }}
        >
          {tickets.map((t) => {
            const totalHours = (t.labor || []).reduce(
              (s, l) => s + (parseFloat(l.hours) || 0),
              0
            );
            return (
              <li key={t.id} style={{ ...styles.card, maxWidth: "780px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <strong>{t.workDate}</strong>
                  <span style={{ fontSize: "13px", color: "#444" }}>
                    {totalHours} hr · {(t.materials || []).length} material(s)
                  </span>
                  {!t.acknowledged && (
                    <span
                      style={{
                        background: "#fff3cd",
                        color: "#9a6b00",
                        fontSize: "11px",
                        padding: "1px 6px",
                        borderRadius: "8px",
                        fontWeight: 700,
                      }}
                    >
                      NEW
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#666",
                      marginLeft: "auto",
                    }}
                  >
                    filed by {t.createdByName || t.createdBy || "—"}
                  </span>
                </div>
                {t.summary && (
                  <div style={{ fontSize: "13px", marginTop: "4px" }}>
                    {t.summary}
                  </div>
                )}
                {(t.labor || []).length > 0 && (
                  <div style={{ marginTop: "6px" }}>
                    <strong style={{ fontSize: "12px", color: "#666" }}>
                      Labor
                    </strong>
                    <ul style={{ margin: "2px 0 0 16px", padding: 0, fontSize: "13px" }}>
                      {(t.labor || []).map((l) => (
                        <li key={l.id}>
                          {l.employeeName || "—"} · {l.hours} hr · {l.date}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(t.materials || []).length > 0 && (
                  <div style={{ marginTop: "6px" }}>
                    <strong style={{ fontSize: "12px", color: "#666" }}>
                      Materials
                    </strong>
                    <ul style={{ margin: "2px 0 0 16px", padding: 0, fontSize: "13px" }}>
                      {(t.materials || []).map((m) => (
                        <li key={m.id}>
                          {m.name}
                          {m.partNumber ? ` (#${m.partNumber})` : ""} ·{" "}
                          {m.qty}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {t.notes && (
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#444",
                      fontStyle: "italic",
                      marginTop: "6px",
                    }}
                  >
                    "{t.notes}"
                  </div>
                )}
                {(t.photos || []).length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      flexWrap: "wrap",
                      marginTop: "6px",
                    }}
                  >
                    {(t.photos || []).map((p) => (
                      <a
                        key={p.url}
                        href={p.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img
                          src={p.url}
                          alt={p.name || "photo"}
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                          }}
                        />
                      </a>
                    ))}
                  </div>
                )}
                {canManage && (
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      marginTop: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    {!t.acknowledged && (
                      <button
                        style={{ ...styles.button, margin: 0 }}
                        onClick={() => acknowledgeTicket(t)}
                      >
                        Acknowledge
                      </button>
                    )}
                    <button
                      style={{
                        ...styles.button,
                        margin: 0,
                        background: "#fee",
                        color: "#a00",
                      }}
                      onClick={() => deleteTicket(t)}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
